import React from "react"
import { useStaticQuery, graphql } from "gatsby"
import Img from "gatsby-image"
import _Grid from "../shared/grid"
import _ProjectDescription from "../shared/project-description"
import VideoProgressBar from "../../design-system/molecules/video-progress-bar"
import useVideoProgressManager from "../shared/hooks/useVideoProgressManager"
import ReactPlayer from "react-player"

import Slider from "react-slick"
import { Block } from "../../design-system/atoms/layout-primitives"
import ContentBlock from "../../design-system/molecules/content-block"
import Button from "../../design-system/atoms/button"
import NewSpacer from "../../design-system/atoms/layout-primitives/spacer"
import {
  CopyText,
  Spacer,
  Container,
  ImageContainer,
  VideoProgressContainer,
  featuresImageStyle,
  featuresBodyStyle,
  adContentImageStyle,
  adContentBodyStyle,
} from "./feature-highlights.style"
import withAnimation from "../../design-system/util/withAnimation"
import { FadeIn } from "../../design-system/styles/animations.styles"

const ProjectDescription = withAnimation(_ProjectDescription)
const Grid = withAnimation(_Grid)

const VideoElementDetails = {
  wayFind: {
    title: "3D Wayfinding",
    start: 0,
    end: 10,
  },
  smartSearch: {
    title: "Smart Search",
    start: 10,
    end: 29,
  },
  smartLabels: {
    title: "Smart Labels",
    start: 29,
    end: 47,
  },
  outdoorMap: {
    title: "Outdoor Map",
    start: 47,
    end: 59,
  },
  mobilePass: {
    title: "Mobile Pass",
    start: 59,
    end: 68,
  },
  topLocations: {
    title: "Top Locations",
    start: 68,
    end: 75,
  },
}

export default function FeatureHighlights() {
  const query = useStaticQuery(graphql`
    query {
      webAppFade: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre Built Applications - Internal Pages/Responsive Web App/webapp_faded_title.svg"
        }
      ) {
        publicURL
      }
      featuresImg: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre Built Applications - Internal Pages/Responsive Web App/video_placeholder.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 720, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      directionsImg: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre Built Applications - Internal Pages/Responsive Web App/turn_by_turn_2.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 720, quality: 90) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      responsiveImg: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre Built Applications - Internal Pages/Responsive Web App/responsive_design.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 720, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      consistantExperienceImages: allFile(
        filter: {
          relativeDirectory: {
            eq: "1_SOLUTIONS/Pre Built Applications - Internal Pages/Responsive Web App/Gallery"
          }
        }
        sort: { fields: base }
      ) {
        edges {
          node {
            id
            base
            childImageSharp {
              fluid(maxWidth: 1920, quality: 100) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
      }
    }
  `)

  const {
    videoPlayerRef,
    videoProgress,
    setVideoProgress,
    seekToTimestamp,
  } = useVideoProgressManager()

  return (
    <Container>
      <Grid>
        <ImageContainer styles={featuresImageStyle}>
          <ReactPlayer
            url={"https://vimeo.com/427471157/ceb019106e"}
            playing={true}
            loop={true}
            volume={0}
            muted={true}
            controls={false}
            width={"100%"}
            height={"100%"}
            onProgress={data => setVideoProgress(data)}
            ref={videoPlayerRef}
            playsinline={true}
          />
        </ImageContainer>
        <VideoProgressContainer>
          <VideoProgressBar
            details={VideoElementDetails.wayFind}
            videoProgress={videoProgress}
            seekMethod={seekToTimestamp}
          />
          <VideoProgressBar
            details={VideoElementDetails.smartSearch}
            videoProgress={videoProgress}
            seekMethod={seekToTimestamp}
          />
          <VideoProgressBar
            details={VideoElementDetails.smartLabels}
            videoProgress={videoProgress}
            seekMethod={seekToTimestamp}
          />
          <VideoProgressBar
            details={VideoElementDetails.outdoorMap}
            videoProgress={videoProgress}
            seekMethod={seekToTimestamp}
          />
          <VideoProgressBar
            details={VideoElementDetails.mobilePass}
            videoProgress={videoProgress}
            seekMethod={seekToTimestamp}
          />
          <VideoProgressBar
            details={VideoElementDetails.topLocations}
            videoProgress={videoProgress}
            seekMethod={seekToTimestamp}
          />
        </VideoProgressContainer>
        <ProjectDescription
          fadeSettings={{ lg: "left" }}
          fadeImage={query.webAppFade.publicURL}
          fadeTitle="Product Features"
          alt="The word 'Web App' displayed behind the title"
          styles={featuresBodyStyle}
          showDivider={false}
        >
          <CopyText animation={FadeIn}>
            Mappedin’s Responsive Web App is focused on providing seamless
            directions to visitors, regardless of device. Features such as smart
            search, mobile pass, and multi-destination wayfinding make it easy
            for users to plan their routes.
          </CopyText>
        </ProjectDescription>
        <Spacer />
        <ImageContainer styles={adContentImageStyle}>
          <ReactPlayer
            url={"https://vimeo.com/488132212/617c75518c"}
            playing={true}
            loop={true}
            volume={0}
            muted={true}
            controls={false}
            width={"100%"}
            height={"400px"}
            playsinline={true}
          />
        </ImageContainer>
        <ProjectDescription
          fadeSettings={{ lg: "left" }}
          fadeImage={query.webAppFade.publicURL}
          fadeTitle="Turn-By-Turn Directions"
          alt="The word 'Web App' displayed behind the title"
          styles={adContentBodyStyle}
          showDivider={false}
        >
          <CopyText animation={FadeIn}>
            We’ve enabled real-time positioning for web or “blue dot” navigation
            on our Responsive Web App. This feature allows visitors to see their
            exact location without additional hardware.
          </CopyText>
        </ProjectDescription>
        <Spacer />
        <Block
          col={{ sm: "1 / -1", md: "3 / -3", lg: "1 / 6" }}
          align={{ lg: "center" }}
        >
          <Slider dots arrows>
            {query.consistantExperienceImages.edges.map(({ node }) => (
              <Img
                key={node.id}
                fluid={node.childImageSharp.fluid}
                imgStyle={{ objectFit: "contain" }}
                alt={node.base}
              />
            ))}
          </Slider>
        </Block>
        <Block
          col={{ sm: "1 / -1", md: "2 / -2", lg: "-6 / -1" }}
          align={{ lg: "center" }}
        >
          <ContentBlock.H3
            title={
              <ProjectDescription
                fadeSettings={{ lg: "left" }}
                fadeImage={query.webAppFade.publicURL}
                fadeTitle="Consistent experience"
                alt="The word 'Web App' displayed behind the title"
                styles={adContentBodyStyle}
                showDivider={false}
              />
            }
            description="Mappedin's solutions are designed to provide a consistent and seamless experience across devices. Users can expect the same look, feel, and function between our web and directory solutions. "
            variants={[null, { textAlign: "center" }, { textAlign: "left" }]}
          />
          <NewSpacer height={{ sm: "32px", lg: "48px" }} />
          <Button.ScrollLink
            to="contact-form"
            smooth
            duration={1000}
            variants={{ md: { margin: "0 auto" }, lg: { margin: "unset" } }}
          >
            Book a Demo
          </Button.ScrollLink>
        </Block>
        <NewSpacer height={{ sm: "48px", lg: "0px" }} />
      </Grid>
    </Container>
  )
}
